/**
 * Wallet Monitor Service — consumes the Laravel "Wallet Monitor" BFF (read-only reporting).
 *
 * Unlike walletService.js (which calls the Go wallet service directly with a short-lived JWT), the
 * monitor BFF lives in Laravel at `/api/wallet-monitor/*` and is authenticated with the SAME Passport
 * `auth:api` bearer the rest of the admin app uses. The admin axios base is `/api/admin`, but the BFF is
 * mounted one level up at `/api/wallet-monitor` (a stable, multi-tier path shared by every front-end), so
 * we use a dedicated instance based at `/api` (the admin base with the trailing `/admin` stripped).
 *
 * Every BFF response is the standard envelope `{ success, message, data }`; these methods return
 * `response.data` (the envelope) and callers read `.data`. The Go-sourced blocks (live balance / top-ups)
 * may be null with `wallet_service_available: false` when the wallet service is down — pages render the
 * local SWT figures regardless and surface that flag (the "balance unavailable" degradation).
 */

import axios from 'axios';
import store from '../store';
import router from '../router';
import { validatePathParam } from '@/services/walletService';

// `/api/admin` → `/api` (the BFF is at /api/wallet-monitor, not under /admin).
const MONITOR_BASE = (process.env.VUE_APP_BASE_API_URL || '').replace(/\/admin\/?$/, '');

const monitorAxios = axios.create({ baseURL: MONITOR_BASE, timeout: 30000 });

// Attach the current Passport bearer on every request (token may be set after this module loads).
monitorAxios.interceptors.request.use((config) => {
  const token = store.getters['User/getToken'];
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = 'Bearer ' + token;
  }
  return config;
});

// 401 → clear auth + bounce to login, mirroring the app's global axios instances (Plugins/Axios.js).
monitorAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user_profile');
      localStorage.removeItem('roleUser');
      store.commit('User/destroyToken');
      if (router.currentRoute.path !== '/') {
        router.push('/').catch(() => {});
      }
    }
    return Promise.reject(error);
  }
);

const ROOT = 'wallet-monitor';

/** Drop empty / null params so the BFF sees only the filters the user actually set. */
function clean(params = {}) {
  const out = {};
  Object.keys(params).forEach((k) => {
    const v = params[k];
    if (v !== null && v !== undefined && v !== '') out[k] = v;
  });
  return out;
}

class WalletMonitorService {
  /** Resolved scope (tier / branch / operator) + Go availability — the FE learns its tier from this. */
  async getAccess(params = {}) {
    const res = await monitorAxios.get(`${ROOT}/access`, { params: clean(params) });
    return res.data;
  }

  /** Fused summary: local SWT net spend + branch owner + COD cross-ref + live Go balance/top-ups. */
  async getSummary(params = {}) {
    const res = await monitorAxios.get(`${ROOT}/summary`, { params: clean(params) });
    return res.data;
  }

  /** Transactions list — source 'shipment' (SWT-native) or 'ledger' (Go proxy, per branch). */
  async getTransactions(params = {}, signal) {
    const res = await monitorAxios.get(`${ROOT}/transactions`, { params: clean(params), signal });
    return res.data;
  }

  /** Per-shipment drill-down (force-delete-safe from the SWT snapshot). */
  async getShipment(bill, params = {}) {
    validatePathParam(bill, 'string');
    const res = await monitorAxios.get(`${ROOT}/shipments/${bill}`, { params: clean(params) });
    return res.data;
  }

  /** Receipts — charge/refund records (+ top-up records for branch/manager tier with a focused branch). */
  async getReceipts(params = {}, signal) {
    const res = await monitorAxios.get(`${ROOT}/receipts`, { params: clean(params), signal });
    return res.data;
  }

  /** Per-employee balance report (warm-cache + poll: status processing/ready). */
  async getEmployeeBalance(params = {}, signal) {
    const res = await monitorAxios.get(`${ROOT}/employee-balance`, { params: clean(params), signal });
    return res.data;
  }

  /** Per-branch balance report + live Go balance overlay (warm-cache + poll). */
  async getBranchBalance(params = {}, signal) {
    const res = await monitorAxios.get(`${ROOT}/branch-balance`, { params: clean(params), signal });
    return res.data;
  }

  /** Queue an async export (report_type: transactions|receipts|balance). Returns { file_id, status }. */
  async requestExport(body = {}) {
    const res = await monitorAxios.post(`${ROOT}/exports`, clean(body));
    return res.data;
  }

  /** The caller's own export files, newest first. */
  async listExports(params = {}) {
    const res = await monitorAxios.get(`${ROOT}/exports`, { params: clean(params) });
    return res.data;
  }

  /**
   * Download a finished export file (owner-scoped on the BFF). When the file is still processing the BFF
   * returns JSON ({ status: 'processing' }) instead of a blob, so the caller polls and retries.
   * @returns {{ ready: true }} after triggering a browser download, or { ready:false, status } while pending.
   */
  async downloadExport(id) {
    validatePathParam(id, 'integer');
    const res = await monitorAxios.get(`${ROOT}/exports/${id}/download`, { responseType: 'blob' });

    // A JSON body (not a binary file) means the export isn't ready yet — surface the status to poll.
    const type = res.headers['content-type'] || '';
    if (type.includes('application/json')) {
      const text = await res.data.text();
      let status = 'processing';
      try {
        status = JSON.parse(text)?.data?.status || status;
      } catch (e) { /* keep default */ }
      return { ready: false, status };
    }

    const blob = res.data;
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = this.filenameFrom(res.headers['content-disposition'], `wallet-export-${id}.xlsx`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    return { ready: true };
  }

  /** Parse a download filename from Content-Disposition (prefers RFC 5987 filename* for Lao/Unicode). */
  filenameFrom(contentDisposition, fallback) {
    if (!contentDisposition) return fallback;
    const utf8 = contentDisposition.match(/filename\*=UTF-8''(.+?)(?:;|$)/i);
    if (utf8) return decodeURIComponent(utf8[1].trim());
    const plain = contentDisposition.match(/filename="?([^";]+)"?/i);
    return plain ? plain[1].trim() : fallback;
  }
}

const walletMonitorService = new WalletMonitorService();
export default walletMonitorService;
